import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { WebsiteChatNotifier } from './WebsiteChatNotifier';
import { WebsiteFormNotifier } from './WebsiteFormNotifier';
import { SocialNotifier } from './SocialNotifier';
import { CallNotifier } from './CallNotifier';

import { cn, formatDate } from '../lib/utils';
import api from '../lib/api';
import { NotificationBell } from './NotificationBell';
import { CommandPalette } from './CommandPalette';
import { AdvancedSearchDialog } from './AdvancedSearchDialog';
import { BannerBar } from './BannerBar';
import {
  LayoutDashboard,
  Users,
  UserPlus,
  Settings,
  LogOut,
  Menu,
  X,
  Sun,
  Moon,
  Zap,
  Bell,
  Search,
  Calendar,
  HardHat,
  UserCog,
  Wrench,
  MessageSquare,
  MessagesSquare,
  Globe,
  Check,
  Clock,
  UserCheck,
  Facebook,
  BarChart3,
  Mail,
  ChevronDown,
  Shield,
  Briefcase,
  PanelLeftClose,
  PanelLeftOpen,
  Archive,
  Activity,
  Headphones,
  MessageCircle,
  Lock,
  Receipt,
  CreditCard,
  MapPin,
  ClipboardList,
  CalendarSync,
  Compass,
  Database,
  UserCircle,
  BookMarked,
  BookUser,
  ShieldCheck,
  FileSpreadsheet,
  ShieldAlert,
  Package,
  PackageCheck,
  Battery,
  FileSignature,
  FileText,
  Star,
  PhoneCall,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { useHrPermissions } from '../hooks/useHrPermissions';

const LOGO_WHITE_URL = 'https://customer-assets.emergentagent.com/job_electrical-tracker-1/artifacts/8ty3m189_Updated%20Logo%20White.png';
const LOGO_DARK_URL = 'https://customer-assets.emergentagent.com/job_f9f64a4c-3451-4815-8703-6feb7c894858/artifacts/mema1897_Updated%20Logo.png';

// Map nav paths to permission sections
const PATH_PERMISSION_MAP = {
  '/dashboard': 'dashboard',
  '/customers': 'customers',
  '/customers/new': 'customers',
  '/postcode-checker': 'customers',
  '/archive': 'customers',
  '/website-leads': 'website_leads',
  '/meta-leads': 'meta_leads',
  '/email-leads': 'meta_leads',
  '/leads-archive': 'meta_leads',
  '/social-inbox': 'social_inbox',
  '/whatsapp': 'whatsapp',
  '/contacts': 'whatsapp',
  '/support': 'support_inbox',
  '/calendar': 'calendar',
  '/my-jobs': 'my_jobs',
  '/engineers': 'engineers',
  '/operations/rams': 'engineers',
  '/installs/adhoc-rams': 'engineers',
  '/reporting': 'reporting',
  '/email-analytics': 'email_analytics',
  '/website-analytics': 'email_analytics',
  '/monitoring': 'monitoring',
  '/pipeline-automation': 'automation',
  '/portal-accounts': 'admin',
  '/employee-approvals': 'admin',
  '/user-management': 'admin',
  '/audit-log': 'admin',
  '/admin/contract-packs': 'admin',
  '/admin/hies': 'admin',
  '/admin/contract-setup': 'admin',
  '/stock': 'admin',
  '/admin/product-catalog': 'admin',
  '/admin/stripe-payments': 'admin',
  '/handover': 'admin',
  '/settings': 'settings',
  '/permissions': 'admin',
};

const navGroups = [
  {
    items: [
      { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    ],
  },
  {
    label: 'Customers',
    icon: Users,
    items: [
      { path: '/customers', label: 'All Customers', icon: Users },
      { path: '/customers/new', label: 'Add Customer', icon: UserPlus },
      { path: '/postcode-checker', label: 'Postcode Checker', icon: MapPin },
      { path: '/archive', label: 'Archive', icon: Archive },
    ],
  },
  {
    label: 'Leads',
    icon: UserPlus,
    badgeKey: 'leads_total_new',
    items: [
      { path: '/website-leads', label: 'Website Leads', icon: Globe, badgeKey: 'leads_wix_new' },
      { path: '/website-chat', label: 'Website Chat', icon: MessagesSquare, badgeKey: 'website_chat_unread' },
      { path: '/meta-leads', label: 'Meta Leads', icon: Facebook, badgeKey: 'leads_meta_new' },
      { path: '/email-leads', label: 'Email Leads', icon: Mail, badgeKey: 'leads_email_new' },
      { path: '/leads-archive', label: 'Leads Archive', icon: Archive },
    ],
  },
  {
    label: 'Communications',
    icon: MessageSquare,
    badgeKey: 'communications_total',
    items: [
      { path: '/social-inbox', label: 'Social Inbox', icon: MessageSquare, badgeKey: 'social_unread' },
      { path: '/social-notifications', label: 'Social Notifications', icon: Bell, badgeKey: 'social_notifications_unread' },
      { path: '/portal-chat', label: 'Portal Chat', icon: MessagesSquare, badgeKey: 'messages_unread' },
      { path: '/whatsapp', label: 'WhatsApp Business', icon: MessageCircle, badgeKey: 'whatsapp_unread' },
      { path: '/contacts', label: 'Contacts', icon: BookUser },
      { path: '/support', label: 'Support Inbox', icon: Headphones, badgeKey: 'support_tickets_open' },
    ],
  },
  {
    // Day-to-day diary management — when work happens and who's doing it.
    label: 'Scheduling',
    icon: Calendar,
    items: [
      { path: '/calendar', label: 'Calendar', icon: Calendar },
      { path: '/smart-schedule', label: 'Smart Schedule', icon: Compass },
      { path: '/my-jobs', label: 'My Jobs', icon: Wrench },
      { path: '/engineers', label: 'Engineers', icon: HardHat },
      { path: '/needs-linking', label: 'Needs Linking', icon: CalendarSync },
    ],
  },
  {
    // Pre-install prep — site survey → RAMS → DNO → stock allocation.
    label: 'Installs',
    icon: Briefcase,
    items: [
      { path: '/surveys', label: 'Surveys', icon: ClipboardList },
      { path: '/survey-form', label: 'Survey Form', icon: ClipboardList },
      { path: '/operations/rams', label: 'RAMS', icon: FileText },
      { path: '/installs/adhoc-rams', label: 'Ad-hoc RAMS', icon: FileText },
      { path: '/dno-applications', label: 'DNO Applications', icon: Zap },
      { path: '/dno-inbox', label: 'DNO Inbox', icon: Mail },
      { path: '/stock', label: 'Stock', icon: Package, adminOnly: true },
      { path: '/handover', label: 'Handover', icon: PackageCheck, adminOnly: true, badgeKey: 'handover_ready' },
    ],
  },
  {
    label: 'Contracts',
    icon: FileSignature,
    adminOnly: true,
    items: [
      { path: '/admin/contract-packs', label: 'Contract Packs Sent', icon: FileSignature },
      { path: '/admin/contract-setup', label: 'Contract Pack Setup', icon: Package },
    ],
  },
  {
    label: 'Analytics',
    icon: BarChart3,
    items: [
      { path: '/reporting', label: 'Reporting', icon: BarChart3 },
      { path: '/call-analytics', label: 'Call Analytics', icon: PhoneCall },
      { path: '/email-analytics', label: 'Email Analytics', icon: Mail },
      { path: '/website-analytics', label: 'Website Analytics', icon: Globe },
      { path: '/monitoring', label: 'Monitoring', icon: Activity },
      { path: '/trustpilot-reviews', label: 'Trustpilot Reviews', icon: Star, requiresTrustpilot: true },
    ],
  },
  {
    // Power-user tools that don't belong to a customer workflow.
    label: 'Tools',
    icon: BookMarked,
    items: [
      { path: '/pipeline-automation', label: 'Automation', icon: Zap },
      { path: '/helpful-links', label: 'Helpful Links', icon: BookMarked },
    ],
  },
  {
    label: 'HR',
    icon: UserCircle,
    hrOnly: true,
    items: [
      { path: '/hr', label: 'Directory', icon: Users },
      { path: '/hr/me', label: 'My Profile', icon: UserCircle },
    ],
  },
  {
    label: 'Admin',
    icon: Shield,
    adminOnly: true,
    items: [
      { path: '/admin/billing', label: 'Billing', icon: Receipt },
      { path: '/admin/stripe-payments', label: 'Stripe Payments', icon: CreditCard },
      { path: '/admin/time-sheets', label: 'Time Sheets', icon: Clock },
      { path: '/admin/backups', label: 'CRM Backups', icon: Database },
      { path: '/admin/product-catalog', label: 'Product Catalog', icon: Package },
      { path: '/admin/access-audit', label: 'Access Audit', icon: ShieldCheck },
      { path: '/admin/gdpr-retention', label: 'GDPR Retention', icon: ShieldAlert },
      { path: '/admin/hies', label: 'HIES', icon: FileSpreadsheet },
      { path: '/portal-accounts', label: 'Portal Accounts', icon: Globe },
      { path: '/employee-approvals', label: 'Employee Approvals', icon: UserCheck },
      { path: '/user-management', label: 'User Admin', icon: UserCog },
      { path: '/audit-log', label: 'Audit Log', icon: Shield },
      { path: '/permissions', label: 'Access Control', icon: Lock },
    ],
  },
  {
    items: [
      { path: '/settings', label: 'Settings', icon: Settings },
    ],
  },
];

export const Layout = ({ children }) => {
  const { user, logout, hasPermission, canAccessPage, pageAccess } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { perms: hrPerms } = useHrPermissions();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [navQuery, setNavQuery] = useState('');
  const [advancedSearchOpen, setAdvancedSearchOpen] = useState(false);
  const [navHighlight, setNavHighlight] = useState(0);
  const navSearchRef = useRef(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [menuCounts, setMenuCounts] = useState({});
  const [trustpilotLive, setTrustpilotLive] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(() => {
    // Auto-expand groups that contain the current active route
    const expanded = {};
    navGroups.forEach((group) => {
      if (group.label && group.items.some(item => location.pathname === item.path)) {
        expanded[group.label] = true;
      }
    });
    return expanded;
  });

  // Build a flat list of every nav item the user is currently allowed to see —
  // used both by the inline sidebar search and by Ctrl/Cmd+K. Keeping the
  // permission/page-access checks here means a hidden page can't be navigated
  // to via search either.
  const allowedNavItems = useMemo(() => {
    if (!user) return [];
    const out = [];
    for (const group of navGroups) {
      if (group.adminOnly && !user?.is_admin) continue;
      if (group.hrOnly && !hrPerms?.can_view_directory) continue;
      for (const item of group.items) {
        if (item.adminOnly && !user?.is_admin) continue;
        if (item.requiresTrustpilot && !trustpilotLive) continue;
        const section = PATH_PERMISSION_MAP[item.path];
        if (section && !hasPermission(section, 'view')) continue;
        if (!canAccessPage(item.path)) continue;
        out.push({ ...item, groupLabel: group.label || 'General' });
      }
    }
    return out;
  }, [user, hrPerms, hasPermission, canAccessPage, trustpilotLive]);

  // Fuzzy-ish filter: match anywhere in label OR group OR path.
  const navResults = useMemo(() => {
    const q = navQuery.trim().toLowerCase();
    if (!q) return [];
    return allowedNavItems
      .map((item) => {
        const label = item.label.toLowerCase();
        const group = (item.groupLabel || '').toLowerCase();
        const path = item.path.toLowerCase();
        let score = 0;
        if (label === q) score = 100;
        else if (label.startsWith(q)) score = 80;
        else if (label.includes(q)) score = 60;
        else if (group.includes(q)) score = 40;
        else if (path.includes(q)) score = 30;
        return { item, score };
      })
      .filter((r) => r.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 8)
      .map((r) => r.item);
  }, [navQuery, allowedNavItems]);

  // Reset highlight when results change
  useEffect(() => { setNavHighlight(0); }, [navQuery]);

  // Global keyboard shortcut: ⌘+Shift+F (or Ctrl+Shift+F) opens Advanced Search
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && (e.key === 'f' || e.key === 'F')) {
        e.preventDefault();
        setAdvancedSearchOpen(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Keyboard handler for the sidebar search input
  const handleNavSearchKey = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setNavHighlight((i) => Math.min(i + 1, Math.max(navResults.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setNavHighlight((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const target = navResults[navHighlight];
      if (target) {
        navigate(target.path);
        setNavQuery('');
        setSidebarOpen(false);
      }
    } else if (e.key === 'Escape') {
      setNavQuery('');
      navSearchRef.current?.blur();
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleGroup = (label) => {
    setExpandedGroups(prev => ({ ...prev, [label]: !prev[label] }));
  };

  // Keep expanded groups in sync with route changes
  useEffect(() => {
    navGroups.forEach((group) => {
      if (group.label && group.items.some(item => location.pathname === item.path)) {
        setExpandedGroups(prev => ({ ...prev, [group.label]: true }));
      }
    });
  }, [location.pathname]);

  // Route-level page-access guard.
  // If the superuser revoked access to the current page for this user, bounce
  // them to the dashboard with a toast. We only redirect when (a) pageAccess
  // has loaded (avoid flicker on first paint) and (b) the route is explicitly
  // registered + denied (unregistered routes like `/customers/abc123` inherit
  // their parent's access via the sidebar — they aren't blocked here).
  useEffect(() => {
    if (!pageAccess) return;
    if (user?.role === 'superuser') return;
    if (location.pathname in pageAccess && pageAccess[location.pathname] === false) {
      toast.error('You do not have access to that page', {
        description: 'Ask an administrator if you need this permission.',
      });
      navigate('/dashboard', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, pageAccess, user?.role]);

  // Poll menu notification counts every 30s + fire a slide-in toast when
  // a NEW lead / message / ticket comes in while the admin is working
  const prevCountsRef = useRef(null);
  useEffect(() => {
    let cancelled = false;
    const fetchCounts = async () => {
      try {
        const { default: api } = await import('../lib/api');
        const r = await api.get('/notifications/menu-counts');
        if (cancelled) return;
        const next = r.data || {};
        const prev = prevCountsRef.current;
        // Only diff after we have a baseline AND the page has been live for >5s
        if (prev) {
          const deltas = [
            { key: 'leads_meta_new',     icon: 'Facebook',     label: 'Meta lead',      path: '/meta-leads',     emoji: 'F' },
            { key: 'leads_wix_new',      icon: 'Globe',        label: 'Website lead',   path: '/website-leads',  emoji: 'W' },
            { key: 'leads_email_new',    icon: 'Mail',         label: 'Email lead',     path: '/email-leads',    emoji: 'E' },
            { key: 'messages_unread',    icon: 'MessagesSquare', label: 'Portal message', path: '/portal-chat',  emoji: 'M' },
            { key: 'whatsapp_unread',    icon: 'MessageCircle', label: 'WhatsApp message', path: '/whatsapp',    emoji: 'W' },
            { key: 'social_unread',      icon: 'MessageSquare', label: 'social message', path: '/social-inbox', emoji: 'S' },
            { key: 'social_notifications_unread', icon: 'Bell', label: 'social notification', path: '/social-notifications', emoji: 'N' },
            { key: 'support_tickets_open', icon: 'Headphones', label: 'Support ticket', path: '/support',        emoji: 'S' },
          ];
          deltas.forEach((d) => {
            const delta = (next[d.key] || 0) - (prev[d.key] || 0);
            if (delta > 0) {
              const word = delta === 1 ? d.label : `${d.label}s`;
              toast.info(
                `${delta} new ${word}`,
                {
                  description: 'Click to open',
                  action: {
                    label: 'View',
                    onClick: () => navigate(d.path),
                  },
                  duration: 12000,
                }
              );
            }
          });
        }
        prevCountsRef.current = next;
        setMenuCounts(next);
      } catch (e) { /* ignore */ }
    };
    fetchCounts();
    const id = setInterval(fetchCounts, 30000);
    const onFocus = () => fetchCounts();
    window.addEventListener('focus', onFocus);
    return () => { cancelled = true; clearInterval(id); window.removeEventListener('focus', onFocus); };
    // eslint-disable-next-line
  }, []);

  // Trustpilot nav item stays hidden until live API credentials are configured.
  useEffect(() => {
    let cancelled = false;
    api.get('/trustpilot/status')
      .then((r) => { if (!cancelled) setTrustpilotLive(!!r.data?.read_live); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const userInitials = user?.name
    ? user.name.split(' ').map(n => n[0]).join('').toUpperCase()
    : 'U';

  /* Reusable count badge for menu items — only renders when count > 0. */
  const CountBadge = ({ count, parent = false }) => {
    const n = Number(count) || 0;
    if (n <= 0) return null;
    return (
      <span
        className={cn(
          'ml-auto inline-flex items-center justify-center min-w-[20px] h-[20px] px-1.5 rounded-full text-[10px] font-bold',
          parent
            ? 'bg-red-500 text-white'
            : 'bg-red-500 text-white'
        )}
        data-testid={`menu-badge-${n}`}
      >
        {n > 99 ? '99+' : n}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <WebsiteChatNotifier />
      <WebsiteFormNotifier />
      <SocialNotifier />
      <CallNotifier />
      <CommandPalette />
      <AdvancedSearchDialog open={advancedSearchOpen} onOpenChange={setAdvancedSearchOpen} />
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-slate-900/50 dark:bg-black/60 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed left-0 top-0 z-40 h-screen bg-slate-900 transition-all duration-300',
          sidebarCollapsed ? 'w-0 lg:w-0 overflow-hidden' : 'w-[280px]',
          !sidebarCollapsed && sidebarOpen ? 'translate-x-0' : !sidebarCollapsed ? 'lg:translate-x-0 -translate-x-full' : ''
        )}
      >
        {/* Logo */}
        <div className="flex items-center gap-3 px-6 py-5 border-b border-slate-800">
          <img 
            src={LOGO_WHITE_URL} 
            alt="Helios Logo" 
            className="h-10 w-auto"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div className="hidden items-center justify-center w-10 h-10 rounded-lg bg-blue-600">
            <Sun className="w-6 h-6 text-white" />
          </div>
          <button
            className="ml-auto text-slate-400 hover:text-white hidden lg:block"
            onClick={() => setSidebarCollapsed(true)}
            title="Hide menu"
            data-testid="collapse-sidebar-btn"
          >
            <PanelLeftClose className="w-5 h-5" />
          </button>
          <button
            className="ml-auto lg:hidden text-slate-400 hover:text-white"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="px-4 py-4 space-y-1 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 160px)' }}>
          {/* Sidebar search — jump straight to any page by typing.
              Falls back to the Cmd/Ctrl+K command palette for power users
              who want to search customers too. */}
          <div className="relative mb-3" data-testid="sidebar-search-wrap">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
            <input
              ref={navSearchRef}
              type="text"
              value={navQuery}
              onChange={(e) => setNavQuery(e.target.value)}
              onKeyDown={handleNavSearchKey}
              placeholder="Jump to page…"
              className="w-full pl-7 pr-12 py-1.5 text-xs rounded-md bg-slate-800/70 text-white placeholder:text-slate-500 border border-slate-700 focus:border-blue-500 focus:bg-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500/30"
              data-testid="sidebar-search-input"
            />
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[9px] text-slate-500 font-mono pointer-events-none select-none">
              {navQuery ? 'ESC' : '⌘K'}
            </span>
          </div>

          {/* Advanced full-CRM search */}
          <button
            onClick={() => setAdvancedSearchOpen(true)}
            className="w-full mb-3 flex items-center justify-between gap-2 px-2.5 py-1.5 text-xs rounded-md bg-slate-800/40 hover:bg-slate-800 text-slate-300 border border-slate-700/60 hover:border-slate-600 transition-colors group"
            data-testid="open-advanced-search-btn"
          >
            <span className="flex items-center gap-2">
              <Search className="w-3.5 h-3.5 text-blue-400" />
              Search the whole CRM…
            </span>
            <span className="text-[9px] font-mono text-slate-500 group-hover:text-slate-400">⇧⌘F</span>
          </button>

          {/* When searching, render a flat result list and hide groups */}
          {navQuery.trim() ? (
            navResults.length === 0 ? (
              <p className="text-xs text-slate-500 italic px-2 py-3" data-testid="sidebar-search-empty">No pages match "{navQuery}"</p>
            ) : (
              <div className="space-y-0.5" data-testid="sidebar-search-results">
                {navResults.map((item, idx) => {
                  const Icon = item.icon;
                  const isHi = idx === navHighlight;
                  return (
                    <button
                      key={item.path}
                      onClick={() => { navigate(item.path); setNavQuery(''); }}
                      onMouseEnter={() => setNavHighlight(idx)}
                      className={cn(
                        'w-full flex items-center gap-2 px-3 py-1.5 rounded text-xs text-left transition-colors',
                        isHi ? 'bg-blue-600 text-white' : 'text-slate-300 hover:bg-slate-800',
                      )}
                      data-testid={`sidebar-search-result-${idx}`}
                    >
                      {Icon && <Icon className="w-3.5 h-3.5 flex-shrink-0" />}
                      <span className="flex-1 truncate">{item.label}</span>
                      <span className={cn('text-[10px]', isHi ? 'text-blue-200' : 'text-slate-500')}>{item.groupLabel}</span>
                    </button>
                  );
                })}
              </div>
            )
          ) : (
          <>
          {navGroups.map((group, groupIdx) => {
            // Filter items by permission + per-page access (superuser controlled)
            const visibleItems = group.items.filter((item) => {
              if (item.adminOnly && !user?.is_admin) return false;
              if (item.requiresTrustpilot && !trustpilotLive) return false;
              const section = PATH_PERMISSION_MAP[item.path];
              if (section && !hasPermission(section, 'view')) return false;
              if (!canAccessPage(item.path)) return false;
              return true;
            });
            if (visibleItems.length === 0) return null;

            // Skip admin-only groups for non-admin users
            if (group.adminOnly && !user?.is_admin) return null;
            if (group.hrOnly && !(hrPerms?.has_hr_access || hrPerms?.is_hr_admin || hrPerms?.is_admin)) return null;

            const isCollapsible = !!group.label;
            const isExpanded = expandedGroups[group.label];
            const hasActiveChild = visibleItems.some(item => location.pathname === item.path);

            return (
              <div key={group.label || groupIdx} className={groupIdx > 0 && isCollapsible ? 'pt-2' : ''}>
                {isCollapsible ? (
                  <>
                    <button
                      onClick={() => toggleGroup(group.label)}
                      className={cn(
                        'flex items-center gap-3 w-full px-4 py-2.5 rounded-lg transition-all duration-200 text-left',
                        hasActiveChild
                          ? 'text-white'
                          : 'text-white hover:text-white'
                      )}
                      data-testid={`nav-group-${group.label.toLowerCase()}`}
                    >
                      <group.icon className="w-5 h-5" />
                      <span className="text-sm font-medium flex-1">{group.label}</span>
                      {group.badgeKey && <CountBadge count={menuCounts[group.badgeKey]} parent />}
                      <ChevronDown className={cn(
                        'w-3.5 h-3.5 transition-transform duration-200',
                        isExpanded ? 'rotate-180' : ''
                      )} />
                    </button>
                    <div className={cn(
                      'overflow-hidden transition-all duration-200',
                      isExpanded ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                    )}>
                      <div className="ml-2 pl-4 border-l border-slate-700/50 space-y-0.5 py-1">
                        {visibleItems.map((item) => {
                          const Icon = item.icon;
                          const isActive = location.pathname === item.path;
                          return (
                            <Link
                              key={item.path}
                              to={item.path}
                              data-testid={`nav-${item.path.replace(/\//g, '-').slice(1)}`}
                              className={cn(
                                'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 text-sm',
                                isActive
                                  ? 'bg-blue-600 text-white'
                                  : 'text-white hover:bg-slate-800'
                              )}
                              onClick={() => setSidebarOpen(false)}
                            >
                              <Icon className="w-5 h-5" />
                              <span className="font-medium">{item.label}</span>
                              {item.badgeKey && <CountBadge count={menuCounts[item.badgeKey]} />}
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  </>
                ) : (
                  visibleItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path;
                    if (item.adminOnly && !user?.is_admin) return null;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        data-testid={`nav-${item.path.replace(/\//g, '-').slice(1)}`}
                        className={cn(
                          'flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 text-sm',
                          isActive
                            ? 'bg-blue-600 text-white'
                            : 'text-white hover:bg-slate-800'
                        )}
                        onClick={() => setSidebarOpen(false)}
                      >
                        <Icon className="w-5 h-5" />
                        <span className="font-medium">{item.label}</span>
                        {item.badgeKey && <CountBadge count={menuCounts[item.badgeKey]} />}
                      </Link>
                    );
                  })
                )}
              </div>
            );
          })}
          </>
          )}
        </nav>

        {/* Bottom section */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-800">
          <div className="flex items-center gap-3 px-2">
            <Avatar className="h-10 w-10">
              <AvatarFallback className="bg-emerald-600 text-white">
                {userInitials}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className={cn('transition-all duration-300', sidebarCollapsed ? 'lg:ml-0' : 'lg:ml-[280px]')}>
        {/* Header */}
        <header className="sticky top-0 z-30 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center justify-between px-4 md:px-6 py-4">
            {/* Menu buttons */}
            <div className="flex items-center gap-2">
              <button
                className="lg:hidden p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
                onClick={() => setSidebarOpen(true)}
                data-testid="mobile-menu-btn"
              >
                <Menu className="w-5 h-5 text-slate-700 dark:text-slate-300" />
              </button>
              {sidebarCollapsed && (
                <button
                  className="hidden lg:flex p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
                  onClick={() => setSidebarCollapsed(false)}
                  title="Show menu"
                  data-testid="expand-sidebar-btn"
                >
                  <PanelLeftOpen className="w-5 h-5 text-slate-700 dark:text-slate-300" />
                </button>
              )}
            </div>

            {/* Advanced search trigger — takes the place of the old broken
                customer search box. Cmd/Ctrl+Shift+F opens the same dialog. */}
            <button
              onClick={() => setAdvancedSearchOpen(true)}
              className="hidden md:flex items-center gap-2 px-3 py-1.5 text-xs rounded-md bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 transition-colors group flex-1 max-w-sm"
              data-testid="header-advanced-search-btn"
              aria-label="Open advanced search"
            >
              <Search className="w-3.5 h-3.5 text-blue-500" />
              <span className="flex-1 text-left text-slate-500 dark:text-slate-400">Search customers, leads, emails…</span>
              <span className="text-[9px] font-mono text-slate-400 dark:text-slate-500">⇧⌘F</span>
            </button>
            <div className="hidden md:flex flex-1" />

            {/* Right side */}
            <div className="flex items-center gap-3">
              {/* Theme Toggle */}
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="h-9 w-9 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
                data-testid="theme-toggle-btn"
                title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {theme === 'dark' ? (
                  <Sun className="w-5 h-5 text-amber-400" />
                ) : (
                  <Moon className="w-5 h-5 text-slate-600" />
                )}
              </Button>

              <NotificationBell />

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="flex items-center gap-2"
                    data-testid="user-menu-btn"
                  >
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-emerald-600 text-white text-sm">
                        {userInitials}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden md:inline text-sm font-medium text-slate-700 dark:text-slate-200">
                      {user?.name}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-3 py-2">
                    <p className="text-sm font-medium">{user?.name}</p>
                    <p className="text-xs text-slate-500">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/settings')}>
                    <Settings className="w-4 h-4 mr-2" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="text-red-600"
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Admin-managed CRM banners */}
        <BannerBar />

        {/* Page content */}
        <main className="p-4 md:p-6 lg:p-8 bg-slate-50 dark:bg-slate-950 min-h-[calc(100vh-73px)]">
          {children}
        </main>
      </div>
    </div>
  );
};
